import { ChangeEvent } from "react";
import {
  AUTH_TYPE,
  AUTH_TYPE_OPTIONS,
  AuthTypeId,
  HTTP_METHOD_OPTIONS,
  HttpMethodId,
} from "../../../utils/integrationConfigData";
import { KeyValueItem } from "../../../utils/keyValueItems";
import KeyValueListEditor from "./KeyValueListEditor";

export interface CredentialFormFields {
  endpoint: string;
  authType: AuthTypeId;
  httpMethod: HttpMethodId;
  requestUrls: KeyValueItem[];
  /** Connection section — saved to details (keyType Header). */
  headers: KeyValueItem[];
  /** Credentials section — saved to credentials (keyType Header). */
  credentialHeaders: KeyValueItem[];
  queryParams: KeyValueItem[];
  requestBody: KeyValueItem[];
  bearerToken: string;
  username: string;
  password: string;
  /** API Key header name (e.g. x-api-key). */
  headerName: string;
  apiKey: string;
}

interface IntegrationCredentialSectionsProps {
  form: CredentialFormFields;
  fieldId: (name: string) => string;
  onFieldChange: (
    field: keyof CredentialFormFields,
  ) => (event: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
  onListChange: (
    field:
      | "requestUrls"
      | "headers"
      | "credentialHeaders"
      | "queryParams"
      | "requestBody",
    items: KeyValueItem[],
  ) => void;
  /** When set, shows Active/Inactive beside Connection title. Omit to hide. */
  connectionActive?: boolean | null;
}

const IntegrationCredentialSections = ({
  form,
  fieldId,
  onFieldChange,
  onListChange,
  connectionActive,
}: IntegrationCredentialSectionsProps) => {
  const isOAuth = form.authType === AUTH_TYPE.OAuth2ClientCredentials;

  const renderAuthFields = () => {
    switch (form.authType) {
      case AUTH_TYPE.BearerToken:
        return (
          <div className="ic-stage-form-field">
            <label className="ic-stage-form-label" htmlFor={fieldId("token")}>
              Bearer token
            </label>
            <input
              id={fieldId("token")}
              type="password"
              className="form-control form-control-sm ic-stage-form-input"
              value={form.bearerToken}
              onChange={onFieldChange("bearerToken")}
              placeholder="Enter bearer token"
              autoComplete="off"
            />
          </div>
        );
      case AUTH_TYPE.Basic:
        return (
          <>
            <div className="ic-stage-form-field">
              <label
                className="ic-stage-form-label"
                htmlFor={fieldId("username")}
              >
                Username
              </label>
              <input
                id={fieldId("username")}
                type="text"
                className="form-control form-control-sm ic-stage-form-input"
                value={form.username}
                onChange={onFieldChange("username")}
              />
            </div>
            <div className="ic-stage-form-field">
              <label
                className="ic-stage-form-label"
                htmlFor={fieldId("password")}
              >
                Password
              </label>
              <input
                id={fieldId("password")}
                type="password"
                className="form-control form-control-sm ic-stage-form-input"
                value={form.password}
                onChange={onFieldChange("password")}
                autoComplete="current-password"
              />
            </div>
          </>
        );
      case AUTH_TYPE.ApiKeyHeader:
        return (
          <div className="ic-api-key-row">
            <div className="ic-stage-form-field ic-api-key-name">
              <label
                className="ic-stage-form-label"
                htmlFor={fieldId("header-name")}
              >
                Key name
              </label>
              <input
                id={fieldId("header-name")}
                type="text"
                className="form-control form-control-sm ic-stage-form-input"
                value={form.headerName}
                onChange={onFieldChange("headerName")}
                placeholder="x-api-key"
                autoComplete="off"
              />
            </div>
            <div className="ic-stage-form-field ic-api-key-value">
              <label
                className="ic-stage-form-label"
                htmlFor={fieldId("api-key")}
              >
                Key value
              </label>
              <input
                id={fieldId("api-key")}
                type="password"
                className="form-control form-control-sm ic-stage-form-input"
                value={form.apiKey}
                onChange={onFieldChange("apiKey")}
                placeholder="Enter API key"
                autoComplete="off"
              />
            </div>
          </div>
        );
      default:
        return (
          <p className="ic-kv-hint mb-0">
            Add Token URL under Request URL. Add Client ID, Client secret, and
            Scope under Request body.
          </p>
        );
    }
  };

  return (
    <div className="ic-stage-form-columns">
      <section className="ic-stage-form-section">
        <div className="ic-connection-header">
          <h3 className="ic-stage-form-section-title mb-0">Connection</h3>
          {connectionActive != null ? (
            <span
              className={`ic-connection-active-badge ${
                connectionActive
                  ? "ic-connection-active-badge--active"
                  : "ic-connection-active-badge--inactive"
              }`}
            >
              {connectionActive ? "Active" : "Inactive"}
            </span>
          ) : null}
        </div>
        <div className="ic-stage-form-grid ic-stage-form-grid-stack">
          <div className="ic-stage-form-field">
            <label className="ic-stage-form-label" htmlFor={fieldId("endpoint")}>
              Endpoint URL
            </label>
            <input
              id={fieldId("endpoint")}
              type="url"
              className="form-control form-control-sm ic-stage-form-input"
              value={form.endpoint}
              onChange={onFieldChange("endpoint")}
              placeholder="https://api.example.com/crd/v1"
            />
          </div>

          <div className="ic-stage-form-field">
            <label className="ic-stage-form-label" htmlFor={fieldId("auth")}>
              Auth type
            </label>
            <select
              id={fieldId("auth")}
              className="form-select form-select-sm ic-stage-form-input ic-stage-form-select"
              value={form.authType}
              onChange={onFieldChange("authType")}
            >
              {AUTH_TYPE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <KeyValueListEditor
            title="Header"
            addLabel="Add header"
            items={form.headers}
            onChange={(items) => onListChange("headers", items)}
            keyPlaceholder="Header name"
            valuePlaceholder="Header value"
            idPrefix={fieldId("connection-headers")}
          />
        </div>
      </section>

      <section className="ic-stage-form-section ic-stage-form-auth ic-credentials-panel">
        <h3 className="ic-stage-form-section-title">Credentials</h3>
        <div className="ic-credential-sections">
          <section className="ic-stage-form-section ic-kv-section ic-credentials-method-field">
            <h3 className="ic-stage-form-section-title">Method</h3>
            <select
              id={fieldId("method")}
              className="form-select form-select-sm ic-stage-form-input ic-stage-form-select ic-kv-method-select"
              value={form.httpMethod}
              onChange={onFieldChange("httpMethod")}
              aria-label="HTTP method"
            >
              {HTTP_METHOD_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </section>

          {isOAuth ? (
            <>
              <KeyValueListEditor
                title="Request URL"
                addLabel="Add request URL"
                items={form.requestUrls}
                onChange={(items) => onListChange("requestUrls", items)}
                keyPlaceholder="Name (e.g. TokenUrl)"
                valuePlaceholder="https://..."
                valueType="url"
                idPrefix={fieldId("request-urls")}
              />

              <KeyValueListEditor
                title="Header"
                addLabel="Add header"
                items={form.credentialHeaders}
                onChange={(items) => onListChange("credentialHeaders", items)}
                keyPlaceholder="Header name"
                valuePlaceholder="Header value"
                idPrefix={fieldId("credential-headers")}
              />

              <KeyValueListEditor
                title="Query Params"
                addLabel="Add parameter"
                items={form.queryParams}
                onChange={(items) => onListChange("queryParams", items)}
                keyPlaceholder="Parameter name"
                valuePlaceholder="Parameter value"
                idPrefix={fieldId("query")}
              />

              <KeyValueListEditor
                title="Request body"
                addLabel="Add body field"
                items={form.requestBody}
                onChange={(items) => onListChange("requestBody", items)}
                keyPlaceholder="Name (e.g. ClientId)"
                valuePlaceholder="Value"
                idPrefix={fieldId("body")}
              />
            </>
          ) : null}

          <div className="ic-credentials-auth-block">
            <h4 className="ic-kv-subsection-title">Authentication</h4>
            <div className="ic-stage-form-grid ic-stage-form-grid-stack">
              {renderAuthFields()}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default IntegrationCredentialSections;
